import { useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { ChevronDown, FunnelX, Search, User } from 'lucide-react'
import { Button } from '@telegram-apps/telegram-ui'
import { useAdminTrpc } from 'shared/api'
import { formatUzsAmount } from 'shared/lib/format'
import { Badge } from 'shared/components/ui/badge'
import { Input } from 'shared/components/ui/input'
import { useDebouncedValue } from 'shared/lib/hooks/useDebouncedValue'
import { useTranslate } from 'shared/lib/i18n'
import { hasAdminPermission } from 'shared/lib/permissions/permissions'
import { useAuthStore } from 'shared/store/auth'
import {
	BUCKET_CHIPS,
	hasAdminLotFilters,
	parseAdminLotFilters,
	replaceAdminLotFilters,
	replaceBucketParam,
	type Bucket,
} from '../lib/state'

type Summary = {
	lots?: {
		all: number
		draft: number
		collecting: number
		ready: number
		purchased: number
		paused: number
		cancelled: number
	}
}

function chipCount(
	summary: Summary | undefined,
	key: Bucket | undefined,
): number | string {
	if (!summary?.lots) return '—'
	return key === undefined ? summary.lots.all : summary.lots[key]
}

export function AdminLots() {
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const [params, setParams] = useSearchParams()
	const { t, prefixT } = useTranslate('pages.adminLots')
	const filters = parseAdminLotFilters(params)
	const { bucket, createdBy, search } = filters
	const debouncedSearch = useDebouncedValue(search?.trim() ?? '', 300)
	const user = useAuthStore((state) => state.user)
	// A seller only ever sees their own lots, so the creator filter is staff-only.
	const canFilterByCreator = user
		? hasAdminPermission(user, 'lots_admin')
		: false

	const [showCreatorFilter, setShowCreatorFilter] = useState(() =>
		Boolean(createdBy),
	)
	const [showSearchFilter, setShowSearchFilter] = useState(() =>
		Boolean(search),
	)
	const hasFilters = hasAdminLotFilters(filters)

	const clearAllFilters = () => {
		replaceAdminLotFilters(setParams, {
			bucket: undefined,
			createdBy: '',
			search: '',
		})
	}

	const { data: summary } = useQuery(trpc.dashboard.Summary.queryOptions())
	const { data: creators } = useQuery({
		...trpc.lots.Creators.queryOptions(),
		enabled: canFilterByCreator,
	})
	const lotLabels: Record<Bucket, string> = {
		draft: t('status.lot.draft'),
		collecting: t('status.lot.collecting'),
		ready: t('status.lot.ready'),
		purchased: t('status.lot.purchased'),
		paused: t('status.lot.paused'),
		cancelled: t('status.lot.cancelled'),
	}

	const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useInfiniteQuery(
			trpc.lots.List.infiniteQueryOptions(
				{ bucket, createdBy, search: debouncedSearch || undefined },
				{
					getNextPageParam: (last) => last.nextCursor,
					initialCursor: 0,
				},
			),
		)

	const lots = useMemo(
		() => (data?.pages ?? []).flatMap((p) => p.items),
		[data],
	)

	const sentinelRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const el = sentinelRef.current
		if (!el) return
		const io = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage)
					void fetchNextPage()
			},
			{ rootMargin: '600px' },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [hasNextPage, isFetchingNextPage, fetchNextPage])

	return (
		<div className='space-y-4 p-4'>
			<div className='flex items-center justify-between gap-2'>
				<h1 className='text-xl font-semibold text-foreground'>
					{prefixT('title')}
				</h1>
				<div className='flex items-center gap-1'>
					{canFilterByCreator && (creators?.length ?? 0) > 1 && (
						<Button
							size='s'
							mode={showCreatorFilter ? 'bezeled' : 'plain'}
							onClick={() => setShowCreatorFilter((v) => !v)}
							aria-label={prefixT('allCreators')}
						>
							<User className='size-5' />
						</Button>
					)}
					<Button
						size='s'
						mode={showSearchFilter ? 'bezeled' : 'plain'}
						onClick={() => setShowSearchFilter((v) => !v)}
						aria-label={prefixT('searchPlaceholder')}
					>
						<Search className='size-5' />
					</Button>
					{hasFilters && (
						<Button
							size='s'
							mode='bezeled'
							onClick={clearAllFilters}
							aria-label={prefixT('clearFilters')}
							className='text-[var(--tgui--link_color,rgb(41,144,255))]'
						>
							<FunnelX className='size-5' />
						</Button>
					)}
					<Button size='s' onClick={() => navigate('/admin/lots/new')}>
						{prefixT('create')}
					</Button>
				</div>
			</div>

			{showSearchFilter && (
				<div className='relative'>
					<Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
					<Input
						type='search'
						autoFocus
						placeholder={prefixT('searchPlaceholder')}
						value={search ?? ''}
						onChange={(e) =>
							replaceAdminLotFilters(setParams, { search: e.target.value })
						}
						className='h-10 pl-9'
					/>
				</div>
			)}

			{canFilterByCreator &&
				showCreatorFilter &&
				(creators?.length ?? 0) > 1 && (
					<div className='relative'>
						<select
							value={createdBy ?? ''}
							onChange={(e) => {
								replaceAdminLotFilters(setParams, {
									createdBy: e.target.value || undefined,
								})
							}}
							className='h-10 w-full appearance-none rounded-md border border-border bg-background pl-3 pr-9 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring'
						>
							<option value=''>{prefixT('allCreators')}</option>
							{creators?.map((creator) => (
								<option key={creator._id} value={creator._id}>
									{creator.name}
								</option>
							))}
						</select>
						<ChevronDown className='pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
					</div>
				)}

			<div className='flex gap-2 overflow-x-auto pb-1'>
				{BUCKET_CHIPS.map((chip) => (
					<button
						key={chip.key ?? 'all'}
						type='button'
						onClick={() => replaceBucketParam(setParams, chip.key)}
						className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
							bucket === chip.key
								? 'border-primary bg-primary text-primary-foreground'
								: 'border-border bg-background text-foreground'
						}`}
					>
						{chip.key ? lotLabels[chip.key] : t('common.all')} (
						{chipCount(summary, chip.key)})
					</button>
				))}
			</div>

			{isLoading && (
				<div className='text-muted-foreground'>{t('common.loading')}</div>
			)}
			{!isLoading && lots.length === 0 && (
				<div className='text-muted-foreground'>{prefixT('empty')}</div>
			)}

			<div className='space-y-3'>
				{lots.map((lot) => (
					<button
						key={lot._id}
						type='button'
						onClick={() => navigate(`/admin/lots/${lot._id}`)}
						className='flex w-full items-center gap-3 rounded-lg border border-border p-3 text-left'
					>
						<img
							src={lot.photoUrls[0]}
							alt={lot.title}
							className='size-14 shrink-0 rounded-md object-cover'
						/>
						<div className='min-w-0 flex-1'>
							<div className='flex items-center justify-between gap-2'>
								<span className='truncate font-medium text-foreground'>
									{lot.title}
								</span>
								<Badge
									variant={
										lot.state === 'ready'
											? 'default'
											: lot.state === 'cancelled'
												? 'destructive'
												: 'secondary'
									}
									className='shrink-0'
								>
									{lotLabels[lot.state as Bucket]}
								</Badge>
							</div>
							<div className='text-sm text-muted-foreground'>
								{t('common.amountUzs', {
									amount: formatUzsAmount(lot.priceUzs),
								})}
								{lot.orderNumber != null && ` · №${lot.orderNumber}`}
							</div>
							<div className='text-sm text-muted-foreground'>
								{prefixT('slotStats', {
									free: String(lot.freeCount),
									paid: String(lot.paidCount),
								})}
							</div>
							{canFilterByCreator && lot.createdByName && (
								<div className='text-sm text-muted-foreground'>
									{prefixT('createdBy', { name: lot.createdByName })}
								</div>
							)}
						</div>
					</button>
				))}

				{isFetchingNextPage && (
					<div className='text-center text-sm text-muted-foreground'>
						{t('common.loading')}
					</div>
				)}
				<div ref={sentinelRef} />
			</div>
		</div>
	)
}
